/**
 * A CLIFF'S FACE, AS THE HILL PAINTER DRAWS IT (openspec world/cliffs). Where the ground drops sheer from one ground row
 * to the next, the screen rows between the rim's surface and the foot's are the face the camera sees: bedded sandstone,
 * in irregular beds with a lit ledge on top of each and a shadow under it, a few joints, faint weathering streaks; the
 * rim's grass hanging over its top; a wet band just above water at its foot. The foot on land is shaded and strewn with
 * scree. Every value comes from the world position, so chunks and bands agree pixel for pixel.
 */

/** A drop from one ground row to the next of more than this many px is sheer: a cliff's face, not a slope. */
export const CLIFF_ROW_PX = 12
/** A drop of more than this from one ground row to the next leaves gaps between its quarter-rows on the screen. */
export const GAP_ROW_PX = 3.5
/** How far south of a face's foot its contact shade and scree reach, in ground rows. */
export const CLIFF_FOOT_REACH = 12

const BASE = [172, 158, 130], LIGHT = [218, 206, 174], DARK = [112, 100, 84], DEEP = [74, 66, 58]
const SCREE = [150, 138, 114]

const hash01 = (x: number, y: number, salt: number): number => {
    let h = (Math.imul(Math.floor(x) | 0, 374761393) + Math.imul(Math.floor(y) | 0, 668265263) + Math.imul(salt, 2246822519)) >>> 0
    h = Math.imul(h ^ (h >>> 13), 1274126177) >>> 0
    return ((h ^ (h >>> 16)) >>> 0) / 4294967296
}

/** Value noise on a `cell`-px lattice, smoothly interpolated. */
const noise = (x: number, y: number, cell: number, salt: number): number => {
    const u = x / cell, v = y / cell, i = Math.floor(u), j = Math.floor(v)
    let fu = u - i, fv = v - j
    fu = fu * fu * (3 - 2 * fu)
    fv = fv * fv * (3 - 2 * fv)
    const a = hash01(i, j, salt), b = hash01(i + 1, j, salt), c = hash01(i, j + 1, salt), d = hash01(i + 1, j + 1, salt)
    const top = a + (b - a) * fu
    return top + (c + (d - c) * fu - top) * fv
}

// The beds' tops, in px of elevation: 6 to 18 px apart, fixed, so every face shows the same strata at the same height.
const BEDS: number[] = (() => {
    const tops: number[] = []
    for (let z = -40, i = 0; z < 400; i++) tops.push(z += 6 + 12 * hash01(i, 0, 77))
    return tops
})()

/** The bed at elevation z: its index and how far up through it z lies (0 its bottom, 1 its top). */
const bedAt = (z: number): { bed: number; t: number } => {
    let lo = 0, hi = BEDS.length - 1
    while (lo < hi) {
        const mid = (lo + hi) >> 1
        if (BEDS[mid] < z) lo = mid + 1
        else hi = mid
    }
    const top = BEDS[lo], bottom = lo > 0 ? BEDS[lo - 1] : top - 12
    return { bed: lo, t: (z - bottom) / Math.max(1, top - bottom) }
}

const mix = (out: number[], a: number[], b: number[], t: number) => {
    for (let c = 0; c < 3; c++) out[c] = a[c] * (1 - t) + b[c] * t
}

/**
 * The colour of a face pixel into `out`: at world column `x`, elevation `z` px, `depth` px below the rim and `rise` px
 * above the foot, `light` the face's light (1 facing the sun's side). Grass hanging from the rim and water at the foot
 * are the caller's.
 */
export const cliffFaceColour = (x: number, z: number, depth: number, rise: number, light: number, out: number[]): void => {
    const zz = z + 3 * Math.sin(x * 0.031 + 1.7) + 4 * (noise(x, 0, 41, 2) - 0.5)
    const { bed, t } = bedAt(zz)
    const tone = 1 + (hash01(bed, 0, 3) - 0.5) * 0.18
    out[0] = BASE[0] * tone * (1 + (hash01(bed, 0, 8) - 0.5) * 0.06)
    out[1] = BASE[1] * tone
    out[2] = BASE[2] * tone
    // A ledge stands out along each bed's top and breaks off along its length; under it, the undercut's shadow.
    if (t > 0.8 - 0.18 * noise(x, bed * 7, 13, 4)) mix(out, out, LIGHT, 0.75)
    else if (t < 0.14 + 0.18 * noise(x, bed * 7 + 3, 9, 5)) mix(out, out, t < 0.05 && noise(x, bed * 7 + 3, 9, 5) > 0.6 ? DEEP : DARK, t < 0.05 ? 1 : 0.75)
    else {
        // A few joints, each within its bed, leaning a little.
        const shift = hash01(bed, 1, 4) * 50, jx = Math.floor((x + shift) / 46)
        const at = jx * 46 - shift + hash01(jx, bed, 6) * 30 + (zz - (BEDS[bed - 1] ?? 0)) * 0.25
        if (Math.abs(x - at) < 0.6 && hash01(jx, bed, 7) < 0.4) mix(out, DARK, DEEP, 0.3)
    }
    // Weathering streaks down the face, and grain.
    const grain = 0.97 + 0.05 * noise(x, z * 0.08, 7, 6) + (hash01(x, z, 5) - 0.5) * 0.09
    // Shade under the rim's overhang, and darker toward the foot.
    const shade = light * grain * (depth > 1.5 && depth < 7 ? 1 - 0.3 * (1 - depth / 7) : 1) * (0.74 + 0.26 * Math.min(1, rise / 26))
    for (let c = 0; c < 3; c++) out[c] *= shade
    // The rim catches the light.
    if (depth < 1.5) mix(out, out, LIGHT, 0.65)
}

/** How many px of the rim's grass hang over the top of the face in column `x`: clumped, none in places. */
export const cliffRimHang = (x: number): number =>
    hash01(x, 0, 42) < 0.7 ? 7 * Math.min(1, Math.max(0, (noise(x, 0, 7, 41) - 0.45) / 0.35)) : 0

/** Darken a face pixel `rise` px above water at its foot: wet and dark just above the waterline. */
export const cliffWet = (rise: number, out: number[]): void => {
    if (rise >= 10) return
    for (let c = 0; c < 3; c++) out[c] = out[c] * 0.58 + [16, 34, 52][c] * 0.2
}

/**
 * A ground pixel `d` rows south of a face's foot on land, into `out` from its own colour `rgb`: the contact shade, and
 * scree in clumps that thin out from the face. False when the pixel is left as the ground draws it.
 */
export const cliffFoot = (x: number, y: number, d: number, rgb: ArrayLike<number>, out: number[]): boolean => {
    if (d >= CLIFF_FOOT_REACH) return false
    const shade = 0.6 + 0.4 * d / CLIFF_FOOT_REACH
    if (hash01(Math.floor(x / 2), Math.floor(y / 2), 54) < 0.55 * (1 - d / CLIFF_FOOT_REACH) * noise(x, y, 7, 53)) {
        const lit = 0.75 + 0.45 * hash01(Math.floor(x / 2), Math.floor(y / 2), 55)
        for (let c = 0; c < 3; c++) out[c] = SCREE[c] * lit
        return true
    }
    for (let c = 0; c < 3; c++) out[c] = rgb[c] * shade
    return true
}
